import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataLineChart } from "web_myplann/Portfolio/Components/DataLineChart/DataLineChart";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import {
	BasicInfo,
	hasBasicInfo,
} from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { InvestmentTool } from "web_myplann/Portfolio/Components/PortfolioTools/InvestmentTool/InvestmentTool";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { getPaidAllLogic } from "web_myplann/Portfolio/Helpers/getPaidAllLogic";
import { normalizeTools } from "web_myplann/Portfolio/Helpers/normalizeTools";
import {
	getPortfolioFrequencyValue,
	getPortfolioValueDate,
} from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

export const Commodities: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const tools = normalizeTools(baseData.tools);

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const { paidAllValue, shouldShowPaidAll } = getPaidAllLogic({ baseData });

	const paidAll = getPortfolioFrequencyValue(
		paidAllValue,
		t(Resources.PortfolioDetail.SharedLabels.Currency[baseData.currency]),
		ClientPortfolioPaymentFrequencyEnum.Monthly,
		t,
		0,
	);

	const basicInfoItems: DataRowCardItem[] = [
		{
			label: t(Resources.PortfolioDetail.SharedLabels.name),
			value: baseData.clientName,
		},
		{
			label: t(Resources.PortfolioDetail.SharedLabels.contractNumber),
			value: baseData.contractNumber,
		},
		{
			label: t(Resources.PortfolioDetail.SharedLabels.producent),
			value: baseData.producer,
		},
		{
			label: t(Resources.PortfolioDetail.SharedLabels.product),
			value: baseData.product,
		},
		{
			label: t(Resources.PortfolioDetail.SharedLabels.paidAll),
			value: shouldShowPaidAll ? paidAll : null,
		},
	];

	return (
		<Stack>
			<DataLineChart
				hideDivider
				totalValue={totalValue}
				totalEvaluationValue={reporting?.summary?.totalEvaluationValue ?? 0}
				totalEvaluationPercent={reporting?.summary?.totalEvaluationPercent ?? 0}
				daysData={daysData}
				currencySign={currencySign}
				toDate={toDate}
				showChartInfo={showChartInfo}
			/>

			{showChartInfo && (
				<>
					<DataRow
						label={t(Resources.Portfolio.Chart.InvestedTogether)}
						value={moneyFormatter(totalInvestedValue, currencySign, "", 2)}
					/>

					<DataRow
						label={t(Resources.Common.ToDate)}
						value={getPortfolioValueDate(daysData, baseData)}
					/>
				</>
			)}

			{daysData.length !== 0 && <SectionDivider />}

			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={
					hasBasicInfo(baseData) ? <BasicInfo data={baseData} /> : null
				}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related, tools)
						.hasAny && tools.length === 0
				}
				items={basicInfoItems}
			/>

			<InvestmentTool
				tools={tools}
				producerInstruments={baseData.producerInstruments}
				toolsSummary={reporting?.toolsSummary}
				portfolioId={baseData.id}
				currencySign={t(
					Resources.PortfolioDetail.SharedLabels.Currency[baseData.currency],
				)}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny &&
					tools.length === 0
				}
			/>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
